import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { parseEmojis } from '../utils/emojiParser';
import './Scripts.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function Scripts() {
  const [scripts, setScripts] = useState([]);
  const [platformFilter, setPlatformFilter] = useState('all');
  const [frameworkFilter, setFrameworkFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [hoveredScript, setHoveredScript] = useState(null);
  const [selectedScript, setSelectedScript] = useState(null);

  useEffect(() => {
    fetchScripts();
  }, []);

  useEffect(() => {
    // Reset framework filter when platform changes
    setFrameworkFilter('all');
  }, [platformFilter]);

  const fetchScripts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/scripts`);
      setScripts(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching scripts:', error);
      setLoading(false);
    }
  };

  const getAvailableFrameworks = () => {
    if (platformFilter === 'redm') {
      return [
        { value: 'all', label: 'All Frameworks' },
        { value: 'RSG', label: 'RSG' },
        { value: 'VORP', label: 'VORP' },
        { value: 'Standalone', label: 'Standalone' }
      ];
    }
    // FiveM or All
    return [
      { value: 'all', label: 'All Frameworks' },
      { value: 'ESX', label: 'ESX' },
      { value: 'QB', label: 'QB-Core' },
      { value: 'QBX', label: 'QBX' },
      { value: 'Standalone', label: 'Standalone' }
    ];
  };

  const filteredScripts = scripts.filter(script => {
    const platformMatch = platformFilter === 'all' || 
                         script.platform === platformFilter || 
                         script.platform === 'both';
    
    if (frameworkFilter === 'all') return platformMatch;
    
    const scriptFrameworks = script.framework ? script.framework.split(',') : [];
    const frameworkMatch = scriptFrameworks.includes(frameworkFilter) ||
                          (scriptFrameworks.length === 0 && frameworkFilter === 'Standalone');
    
    return platformMatch && frameworkMatch;
  });

  return (
    <div className="scripts-page page-container">
      <div className="container">
        <div className="page-header fade-in">
          <h1 className="page-title">Our Scripts</h1>
          <p className="page-subtitle">Browse our collection of premium FiveM and RedM scripts</p>
        </div>

        <div className="filter-buttons glass">
          <div className="filter-row">
            <button 
              className={`filter-btn ${platformFilter === 'all' ? 'active' : ''}`}
              onClick={() => setPlatformFilter('all')}
            >
              All Scripts
            </button>
            <button 
              className={`filter-btn ${platformFilter === 'fivem' ? 'active' : ''}`}
              onClick={() => setPlatformFilter('fivem')}
            >
              FiveM
            </button>
            <button 
              className={`filter-btn ${platformFilter === 'redm' ? 'active' : ''}`}
              onClick={() => setPlatformFilter('redm')}
            >
              RedM
            </button>
          </div>

          <div className="filter-divider"></div>

          <div className="filter-row">
            {getAvailableFrameworks().map(fw => (
              <button
                key={fw.value}
                className={`filter-btn framework ${frameworkFilter === fw.value ? 'active' : ''}`}
                onClick={() => setFrameworkFilter(fw.value)}
              >
                {fw.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading scripts...</div>
        ) : filteredScripts.length === 0 ? (
          <div className="no-scripts glass">
            <h3>No scripts available yet</h3>
            <p>Check back soon for new releases!</p>
          </div>
        ) : (
          <div className="scripts-grid">
            {filteredScripts.map((script) => {
              console.log('Rendering script:', script.title, 'thumbnail:', script.thumbnail);
              return (
              <div 
                key={script.id} 
                className={`script-card fade-in ${hoveredScript === script.id ? 'hovered' : ''}`}
                onMouseEnter={() => setHoveredScript(script.id)}
                onMouseLeave={() => setHoveredScript(null)}
                onClick={() => script.features && setSelectedScript(script)}
              >
                <div className="script-thumbnail">
                  {script.thumbnail && (
                    <img src={`${API_URL}${script.thumbnail}`} alt={script.title} />
                  )}
                </div>
                
                <div className="script-content">
                  <div className="script-header">
                    <h3 className="script-title">{script.title}</h3>
                    <div className="script-badges">
                      <span className={`platform-badge ${script.platform}`}>
                        {script.platform === 'both' ? 'FiveM & RedM' : script.platform.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  
                  {script.description && (
                    <p className="script-description">{parseEmojis(script.description)}</p>
                  )}
                  
                  {script.framework && (
                    <div className="script-frameworks">
                      <h4>Frameworks</h4>
                      <div className="frameworks-list">
                        {script.framework.split(',').map((fw, idx) => (
                          <span key={idx} className="framework-badge">
                            {fw.trim() === 'QB' ? 'QB-Core' : fw.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {script.dependencies && (
                    <div className="script-dependencies">
                      <h4>Dependencies</h4>
                      <div className="dependencies-list">
                        {script.dependencies.split('\n').filter(d => d.trim()).map((dep, idx) => (
                          <span key={idx} className="dependency-tag">{dep.trim()}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="script-footer">
                  <span className="script-price">
                    {script.price ? `$${script.price}` : 'Free'}
                  </span>
                  <div className="script-buttons">
                    {script.preview_url && (
                      <a 
                        href={script.preview_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn btn-secondary btn-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        🎬 Preview
                      </a>
                    )}
                    {script.tebex_url && (
                      <a 
                        href={script.tebex_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn btn-primary btn-sm"
                      >
                        Purchase
                      </a>
                    )}
                  </div>
                </div>

                {/* Hover Overlay - končí těsně nad footerem */}
                {script.features && hoveredScript === script.id && (
                  <div className="card-hover-overlay">
                    <div className="hover-icon">👆</div>
                    <div className="hover-text">Show Features</div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}

        {/* Features Modal */}
        {selectedScript && (
          <div className="modal-backdrop" onClick={() => setSelectedScript(null)}>
            <div className="features-modal glass" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>✨ {selectedScript.title} - Features</h2>
                <button className="close-btn" onClick={() => setSelectedScript(null)}>✕</button>
              </div>
              <div className="modal-content">
                {selectedScript.features.split('\n').filter(f => f.trim()).map((feature, idx) => (
                  <div key={idx} className="feature-item-modal">{parseEmojis(feature)}</div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Scripts;
